import { useState } from 'react';
import { motion } from 'framer-motion';

const serviceOptions = [
  'WOF Inspection',
  'Engine Servicing',
  'Tyre Replacement or Repair',
  '3D Laser Wheel Alignment',
  'Brake, Steering or Suspension',
  'ECU Chiptuning',
  'Transmission Servicing',
  'Full Diagnostic Report',
  'Battery Replacement',
  'Other',
];

const inputStyle = {
  width: '100%',
  background: '#F9F9F9',
  border: '1.5px solid #e0e0e0',
  borderRadius: '8px',
  padding: '0.75rem 1rem',
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.9rem',
  color: '#1A1A1A',
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.825rem',
  fontWeight: 600,
  color: '#1A1A1A',
  display: 'block',
  marginBottom: '0.4rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', service: '', date: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFocus = e => { e.target.style.borderColor = '#F5C300'; };
  const handleBlur = e => { e.target.style.borderColor = '#e0e0e0'; };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': 'contact-form',
          ...formData,
        }).toString(),
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{ paddingTop: '80px' }}
    >
      {/* ── HERO ──────────────────────────────────────────────── */}
      <section style={{ background: '#1A1A1A', padding: 'clamp(3.5rem, 8vw, 6rem) 1.5rem' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span style={{ fontFamily: 'Inter, sans-serif', color: '#F5C300', fontWeight: 600, fontSize: '0.8rem', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              Get in Touch
            </span>
            <h1 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(3rem, 7vw, 5.5rem)', color: '#ffffff', margin: '0.3rem 0 1rem', lineHeight: 1, letterSpacing: '0.02em' }}>
              Book a <span style={{ color: '#F5C300' }}>Service</span>
            </h1>
            <div style={{ width: '60px', height: '4px', background: '#F5C300', marginBottom: '1.5rem' }} />
            <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: '520px', margin: 0 }}>
              Fill in the form below and we will be in touch within 24 hours to confirm your booking. Alternatively, give us a call.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── FORM + DETAILS ────────────────────────────────────── */}
      <section style={{ padding: 'clamp(3rem, 6vw, 5rem) 1.5rem', background: '#ffffff' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 'clamp(2.5rem, 5vw, 4rem)' }}>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                style={{ background: '#F9F9F9', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', border: '2px solid #F5C300' }}
              >
                <div style={{ color: '#F5C300', marginBottom: '1.25rem', display: 'flex', justifyContent: 'center' }}>
                  <svg width="56" height="56" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <h3 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: '2rem', color: '#1A1A1A', margin: '0 0 0.75rem', letterSpacing: '0.03em' }}>
                  Thanks, we will be in touch!
                </h3>
                <p style={{ fontFamily: 'Inter, sans-serif', color: '#666', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                  We will be in touch within 24 hours to confirm your booking. We look forward to looking after your vehicle.
                </p>
              </motion.div>
            ) : (
              <>
                <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: '#1A1A1A', margin: '0 0 0.4rem', letterSpacing: '0.02em' }}>
                  Make a Booking
                </h2>
                <div style={{ width: '40px', height: '4px', background: '#F5C300', marginBottom: '2rem' }} />

                <form
                  name="contact-form"
                  method="POST"
                  data-netlify="true"
                  onSubmit={handleSubmit}
                >
                  <input type="hidden" name="form-name" value="contact-form" />

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    <div>
                      <label htmlFor="name" style={labelStyle}>Full Name *</label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        required
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" style={labelStyle}>Phone *</label>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        required
                        placeholder="0210 812 1239"
                        value={formData.phone}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.25rem' }}>
                    <label htmlFor="email" style={labelStyle}>Email Address *</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '1.25rem', marginBottom: '1.25rem' }}>
                    <div>
                      <label htmlFor="service" style={labelStyle}>Service Required *</label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        style={{ ...inputStyle, cursor: 'pointer' }}
                      >
                        <option value="">Select a service...</option>
                        {serviceOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="date" style={labelStyle}>Preferred Date</label>
                      <input
                        id="date"
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.75rem' }}>
                    <label htmlFor="message" style={labelStyle}>Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      placeholder="Tell us about your vehicle and any issues you have noticed..."
                      value={formData.message}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: '130px' }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                      background: loading ? '#cca800' : '#F5C300', color: '#1A1A1A',
                      fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700,
                      fontSize: '1.05rem', letterSpacing: '0.08em',
                      padding: '0.85rem 2.25rem', borderRadius: '9999px',
                      border: 'none', cursor: loading ? 'wait' : 'pointer',
                      transition: 'background 0.25s, transform 0.25s', width: '100%',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#e6b800'; e.currentTarget.style.transform = 'scale(1.02)'; }}}
                    onMouseLeave={e => { e.currentTarget.style.background = loading ? '#cca800' : '#F5C300'; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    {loading ? 'SENDING...' : 'SUBMIT BOOKING REQUEST'}
                    {!loading && (
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>

          {/* Business details */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <h2 style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 800, fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', color: '#1A1A1A', margin: '0 0 0.4rem', letterSpacing: '0.02em' }}>
              Find Us
            </h2>
            <div style={{ width: '40px', height: '4px', background: '#F5C300', marginBottom: '2rem' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
              {[
                {
                  icon: (
                    <svg width="22" height="22" fill="none" stroke="#F5C300" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                  label: 'Address',
                  value: '12 Wall Place, Kenepuru, Porirua 5022, New Zealand',
                },
                {
                  icon: (
                    <svg width="22" height="22" fill="none" stroke="#F5C300" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  ),
                  label: 'Phone',
                  value: '0210 812 1239',
                },
                {
                  icon: (
                    <svg width="22" height="22" fill="none" stroke="#F5C300" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  label: 'Email',
                  value: 'sales@northcitytyres.co.nz',
                },
                {
                  icon: (
                    <svg width="22" height="22" fill="none" stroke="#F5C300" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                  label: 'Hours',
                  value: 'Mon to Thu: 8:00am to 5:00pm\nFri: 8:00am to 4:30pm\nSat: 8:30am to 1:00pm\nSun: Closed',
                },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ flexShrink: 0, marginTop: '2px' }}>{item.icon}</div>
                  <div>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.8rem', color: '#1A1A1A', margin: '0 0 0.2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {item.label}
                    </p>
                    {item.value.split('\n').map((line, j) => (
                      <p key={j} style={{ fontFamily: 'Inter, sans-serif', color: '#555', fontSize: '0.9rem', margin: '0', lineHeight: 1.6 }}>
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* MTA badge */}
            <div style={{ background: '#1A1A1A', borderRadius: '12px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ color: '#F5C300', flexShrink: 0 }}>
                <svg width="36" height="36" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.25C17.25 22.15 21 17.25 21 12V7L12 2zm-2 13l-4-4 1.41-1.41L10 12.17l6.59-6.59L18 7l-8 8z" />
                </svg>
              </div>
              <div>
                <p style={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: '#F5C300', margin: '0 0 0.15rem', letterSpacing: '0.06em' }}>MTA ASSURED WORKSHOP</p>
                <p style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', margin: 0 }}>Trusted, certified, and committed to quality</p>
              </div>
            </div>

            {/* Google Maps */}
            <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1.5px solid #e0e0e0' }}>
              <iframe
                src="https://maps.google.com/maps?q=North+City+Service+and+Tyre+Centre+Porirua&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="400"
                style={{ border: 0, display: 'block' }}
                allowFullScreen=""
                loading="lazy"
                title="North City Service and Tyre Centre location map"
              />
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;
